import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Calendar as CalIcon, Clock } from "@phosphor-icons/react";

export default function BookAppointment() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [slot, setSlot] = useState("");
  const [treatment, setTreatment] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const slots = useMemo(() => {
    if (!date) return [];
    const out = [];
    for (let hour = 10; hour < 18; hour++) {
      for (const minute of [0, 30]) {
        out.push(`${date} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`);
      }
    }
    return out;
  }, [date]);

  const book = async () => {
    if (!slot) {
      toast.error("Pick a preferred slot");
      return;
    }

    setBusy(true);
    try {
      await api.post("/portal/book", {
        starts_at: slot,
        duration_min: 30,
        treatment,
        notes,
      });
      toast.success("Appointment request sent. Our team will assign the right doctor and confirm shortly.");
      navigate("/portal/appointments");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const dateOptions = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, []);

  return (
    <div data-testid="book-appointment-page" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <CalIcon size={28} weight="duotone" color="#E60000" />
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: "Manrope, sans-serif" }}>
          Book an appointment
        </h1>
      </div>
      <p className="text-sm text-zinc-500 mb-8">
        Choose your preferred date and time. Our team will assign the right doctor and confirm by SMS or WhatsApp.
      </p>

      <Step n="1" title="Pick a preferred date">
        <div className="flex flex-wrap gap-2">
          {dateOptions.map((d) => {
            const dt = new Date(d);
            const selected = d === date;
            return (
              <button
                key={d}
                onClick={() => {
                  setDate(d);
                  setSlot("");
                }}
                className={`px-3 py-2 border rounded-sm text-center min-w-[60px] transition-colors ${
                  selected ? "border-[#E60000] bg-red-50" : "border-zinc-200 hover:border-zinc-900"
                }`}
                data-testid={`book-date-${d}`}
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                  {dt.toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <div className="text-lg font-black tracking-tighter" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {dt.getDate()}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {dt.toLocaleDateString(undefined, { month: "short" })}
                </div>
              </button>
            );
          })}
        </div>
      </Step>

      <Step n="2" title="Choose a preferred slot" disabled={!date}>
        {slots.length === 0 ? (
          <div className="text-sm text-zinc-500 bg-white border border-zinc-200 rounded-sm p-6 text-center">
            <Clock size={20} weight="duotone" color="#E60000" className="inline mb-1" />
            <div>No slots available on this date. Try another day.</div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => {
              const selected = s === slot;
              return (
                <button
                  key={s}
                  onClick={() => setSlot(s)}
                  className={`px-3 py-2 border rounded-sm text-sm font-bold ${
                    selected ? "border-[#E60000] bg-[#E60000] text-white" : "border-zinc-200 hover:border-zinc-900 text-zinc-900"
                  }`}
                  data-testid={`book-slot-${s}`}
                >
                  {s.slice(11, 16)}
                </button>
              );
            })}
          </div>
        )}
      </Step>

      <Step n="3" title="Anything we should know?" disabled={!slot}>
        <div className="space-y-3">
          <input
            value={treatment}
            onChange={(e) => setTreatment(e.target.value)}
            className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-zinc-900"
            placeholder="Treatment / concern (e.g. Cleaning, Implant consult)"
            data-testid="book-treatment"
          />
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-zinc-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-zinc-900"
            placeholder="Any details (optional)"
            data-testid="book-notes"
          />
          <button
            onClick={book}
            disabled={busy || !slot}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-6 py-3 text-sm disabled:opacity-60"
            data-testid="book-submit"
          >
            {busy ? "Sending..." : "Send appointment request"} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </Step>
    </div>
  );
}

function Step({ n, title, disabled, children }) {
  return (
    <div className={`mt-6 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      <div className="flex items-center gap-3 mb-3">
        <span
          className="w-7 h-7 rounded-sm bg-zinc-900 text-white font-black text-sm flex items-center justify-center"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          {n}
        </span>
        <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}
